import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function AdminPricingMenu() {
    const { token } = useAuth();
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchPlans = React.useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/admin_manage_pricing.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                setPlans(data);
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to fetch plans' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Network error fetching plans' });
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleSave = async (e) => {
        e.preventDefault();
        setMessage({ type: 'info', text: 'Saving...' });

        const url = 'https://healthcard.searchisend.com/health_api/admin_manage_pricing.php';
        const method = currentPlan.id ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(currentPlan)
            });
            const data = await response.json();

            if (response.ok) {
                setMessage({ type: 'success', text: data.message });
                setIsEditing(false);
                setCurrentPlan(null);
                fetchPlans();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to save plan' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Network error saving plan' });
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-brand-secondary">Manage Pricing Categories</h2>
                {!isEditing && (
                    <button
                        onClick={() => {
                            setCurrentPlan({ categoryName: '', mrp: '', salePrice: '', validityMonths: 12, isActive: 1 });
                            setIsEditing(true);
                            setMessage({ type: '', text: '' });
                        }}
                        className="bg-brand-primary hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded-xl shadow-md transition-colors"
                    >
                        + Create New Plan
                    </button>
                )}
            </div>

            {message.text && (
                <div className={`mb-4 p-4 rounded-xl font-medium ${message.type === 'error' ? 'bg-red-50 text-red-600' : message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-600'}`}>
                    {message.text}
                </div>
            )}

            {isEditing ? (
                <form onSubmit={handleSave} className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-8">
                    <h3 className="text-lg font-bold text-brand-secondary mb-4">{currentPlan.id ? 'Edit Plan' : 'Create New Plan'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Category Name</label>
                            <input type="text" value={currentPlan.categoryName} onChange={e => setCurrentPlan({ ...currentPlan, categoryName: e.target.value })} className="w-full p-2.5 border rounded-lg" required placeholder="e.g. Senior Citizen" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Original MRP (₹)</label>
                            <input type="number" step="0.01" value={currentPlan.mrp} onChange={e => setCurrentPlan({ ...currentPlan, mrp: e.target.value })} className="w-full p-2.5 border rounded-lg" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Sale Price checkout (₹)</label>
                            <input type="number" step="0.01" value={currentPlan.salePrice} onChange={e => setCurrentPlan({ ...currentPlan, salePrice: e.target.value })} className="w-full p-2.5 border rounded-lg" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Validity (Months)</label>
                            <input type="number" value={currentPlan.validityMonths} onChange={e => setCurrentPlan({ ...currentPlan, validityMonths: e.target.value })} className="w-full p-2.5 border rounded-lg" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-1">Status</label>
                            <select value={currentPlan.isActive} onChange={e => setCurrentPlan({ ...currentPlan, isActive: e.target.value })} className="w-full p-2.5 border rounded-lg" required>
                                <option value="1">Active (Published)</option>
                                <option value="0">Inactive (Hidden)</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-4 mt-6">
                        <button type="submit" className="bg-brand-secondary hover:bg-slate-800 text-white font-bold py-2.5 px-6 rounded-xl">Save Plan</button>
                        <button type="button" onClick={() => setIsEditing(false)} className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold py-2.5 px-6 rounded-xl">Cancel</button>
                    </div>
                </form>
            ) : (
                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="text-center py-8 text-slate-500">Loading plans...</div>
                    ) : (
                        <table className="min-w-full bg-white border border-slate-200 rounded-xl overflow-hidden">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="py-3 px-4 text-left font-bold text-slate-600 text-sm">Category</th>
                                    <th className="py-3 px-4 text-left font-bold text-slate-600 text-sm">Sale/MRP</th>
                                    <th className="py-3 px-4 text-left font-bold text-slate-600 text-sm">Validity</th>
                                    <th className="py-3 px-4 text-left font-bold text-slate-600 text-sm">Status</th>
                                    <th className="py-3 px-4 text-right font-bold text-slate-600 text-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {plans.map(plan => (
                                    <tr key={plan.id} className="hover:bg-slate-50">
                                        <td className="py-3 px-4 font-bold text-brand-secondary">{plan.categoryName}</td>
                                        <td className="py-3 px-4 text-slate-600">₹{parseInt(plan.salePrice)} <span className="text-xs line-through text-slate-400">₹{parseInt(plan.mrp)}</span></td>
                                        <td className="py-3 px-4 text-slate-600">{plan.validityMonths} m</td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${Number(plan.isActive) === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {Number(plan.isActive) === 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                onClick={() => { setCurrentPlan(plan); setIsEditing(true); setMessage({ type: '', text: '' }); }}
                                                className="text-brand-primary font-bold hover:underline text-sm"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {plans.length === 0 && (
                                    <tr><td colSpan="5" className="text-center py-6 text-slate-500">No pricing plans found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminPricingMenu;
